import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Drawer,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Typography,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Avatar,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useConversations } from '../hooks/useConversations';
import { useConversationsContext } from '../contexts/ConversationsContext';
import { getWebSocketManager } from '../utils/websocket';
import { formatConversationDate } from '../utils/dateFormatter';
import SettingsModal from './settings/SettingsModal';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  onNewChat: () => void;
  onSelectConversation?: (conversationId: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose,
  onNewChat,
  onSelectConversation,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Get conversations from context
  const { conversations, loading, error, hasMore, loadMore } = useConversations();
  const { updateConversationTitle, renameConversation } = useConversationsContext();

  // Inline rename
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);

  const startRename = (conversationId: string, currentTitle: string) => {
    setEditingId(conversationId);
    setDraftTitle(currentTitle);
    setRenameError(null);
  };

  const commitRename = async () => {
    if (!editingId) return;
    const conversationId = editingId;
    const title = draftTitle.trim();
    const current = conversations.find(conv => conv.conversationId === conversationId)?.title;
    setEditingId(null);
    if (!title || title === current) return;
    try {
      await renameConversation(conversationId, title);
    } catch {
      setRenameError('Failed to rename conversation');
    }
  };

  // Load the next page when the end of the list scrolls into view
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loading) {
          loadMore();
        }
      },
      { root: scrollRef.current, rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore, conversations.length]);

  // Set up WebSocket title updates
  useEffect(() => {
    const wsManager = getWebSocketManager();
    
    const unsubscribe = wsManager.onTitleUpdate(({ conversationId, title }) => {
      console.log('Sidebar received title update for conversation:', conversationId, 'with title:', title);
      updateConversationTitle(conversationId, title);
      console.log('Called updateConversationTitle in context');
    });

    return unsubscribe;
  }, [updateConversationTitle]);

  const drawerWidth = 280;

  const sidebarContent = (
    <Box
      sx={{
        width: drawerWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header with New Chat button */}
      <Box sx={{ p: 2 }}>
        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<AddIcon />}
          onClick={() => {
            onNewChat();
            if (isMobile) onClose();
          }}
          sx={{
            borderRadius: 2,
            py: 1.5,
            borderColor: 'divider',
            color: 'text.primary',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'primary.50',
            },
          }}
        >
          New conversation
        </Button>
      </Box>

      <Divider />

      {/* Previous Chats */}
      <Box ref={scrollRef} sx={{ flexGrow: 1, overflowY: 'auto', px: 1 }}>
        {loading && conversations.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error && conversations.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              color="error"
              sx={{ fontSize: '0.875rem' }}
            >
              Failed to load conversations
            </Typography>
          </Box>
        ) : conversations.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              color="text.disabled"
              sx={{ 
                fontStyle: 'italic',
                fontSize: '0.875rem'
              }}
            >
              No conversations yet
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 1 }}>
            {renameError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', px: 2, pb: 1 }}>
                {renameError}
              </Typography>
            )}
            {conversations.map((conversation) => {
              const title = conversation.title || 'New Conversation';
              const editing = editingId === conversation.conversationId;
              return (
              <ListItemButton
                key={conversation.conversationId}
                onClick={() => {
                  if (editing) return;
                  if (onSelectConversation) {
                    onSelectConversation(conversation.conversationId);
                  }
                  if (isMobile) onClose();
                }}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  mx: 1,
                  pr: 1,
                  '&:hover': {
                    bgcolor: 'primary.50',
                  },
                  '&:hover .rename-button, & .rename-button:focus-visible': {
                    opacity: 1,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ChatIcon fontSize="small" color="action" />
                </ListItemIcon>
                {editing ? (
                  <TextField
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') commitRename();
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    onBlur={commitRename}
                    autoFocus
                    size="small"
                    variant="standard"
                    fullWidth
                    inputProps={{ maxLength: 120, 'aria-label': 'Conversation title' }}
                  />
                ) : (
                  <>
                    <ListItemText
                      primary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {title}
                        </Typography>
                      }
                      secondary={
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {formatConversationDate(conversation.lastActivity)}
                        </Typography>
                      }
                    />
                    <IconButton
                      className="rename-button"
                      size="small"
                      aria-label="Rename conversation"
                      onClick={(e) => {
                        e.stopPropagation();
                        startRename(conversation.conversationId, title);
                      }}
                      // Touch screens have no hover, so the button stays visible there
                      sx={{ opacity: isMobile ? 1 : 0, transition: 'opacity 0.15s' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </ListItemButton>
              );
            })}
            {hasMore && (
              <Box ref={sentinelRef} sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                {loading && <CircularProgress size={20} />}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Divider />

      {/* Profile Section */}
      <Box sx={{ p: 2 }}>
        <ListItemButton
          onClick={() => {
            setSettingsOpen(true);
            if (isMobile) onClose();
          }}
          sx={{
            borderRadius: 2,
            '&:hover': {
              bgcolor: 'primary.50',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              <PersonIcon fontSize="small" />
            </Avatar>
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Profile & Settings
              </Typography>
            }
          />
        </ListItemButton>
      </Box>

      {/* Settings Modal */}
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': {
            top: '64px', // Start below the AppBar
            height: 'calc(100vh - 64px)',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: drawerWidth,
        height: 'calc(100vh - 64px)', // Account for AppBar height
        position: 'fixed',
        left: 0,
        top: '64px', // Start below the AppBar
        borderRight: 1,
        borderColor: 'divider',
        zIndex: theme.zIndex.drawer,
      }}
    >
      {sidebarContent}
    </Paper>
  );
};

export default Sidebar;
